/**
 * The family key: the one secret the server must never hold.
 *
 * Names and avatar part ids, transaction reasons, goal titles, quick-action
 * labels, the family's own name and every device label are ciphertext by the
 * time they reach the API. This module is what makes them readable again, and
 * it is the only place in the app that touches a key.
 *
 * THE SHAPE, as chosen:
 *
 *   family key (random AES-256-GCM)
 *     |
 *     +-- wrapped under the family code            <- any parent, any device
 *     +-- wrapped to a parent device's public key  <- "Ask another device"
 *     +-- wrapped under a display's link code      <- the wall
 *
 * The key is random and derived from nothing. It is NOT derived from the
 * Cognito password, and that is the load-bearing decision: Cognito's
 * forgot-password flow happens on AWS's own page, outside this app, so a
 * password reset would silently destroy every name in the family with no
 * chance to re-wrap and no warning. Wrapping instead of deriving means a
 * password change is simply irrelevant.
 *
 * WHAT IS NOT HERE, deliberately: no key escrow, no "email me my key", no
 * server-side copy of anything. If every wrapping is lost the names and
 * avatars are gone for good. The ledger, the goals and every balance survive,
 * because none of them was ever encrypted -- that trade is the whole design.
 *
 * ON EXTRACTABILITY, and a correction. An earlier version of this module
 * imported the key NON-extractable and said so proudly: the key could be used
 * while the page was open but its raw bytes could never be read back out.
 * That is a genuine XSS mitigation and it is not compatible with this app.
 * `crypto.subtle.wrapKey` requires the key it wraps to be extractable, and
 * wrapping is not an edge case here -- it is how a second parent is invited
 * and how the wall display is linked. A non-extractable key can never be
 * handed to anybody, which in a FAMILY app means the design was wrong rather
 * than the requirement.
 *
 * So the key is extractable, and the honest statement of what that costs is:
 * script injection on this origin can read the family key out of IndexedDB
 * and take it away, not merely use it while the tab is open. What stands
 * between the app and that is the CSP in 30-web.yaml, no third-party script
 * anywhere in the bundle, and no runtime network request to anything but the
 * API -- which is the same set of properties the "hold nothing" rule already
 * depends on, rather than a new one invented for this.
 */

const KDF_ITERATIONS = 600_000; // OWASP's PBKDF2-HMAC-SHA256 floor.
const SALT_BYTES = 16;
const NONCE_BYTES = 12;

/** A wrapped family key, as stored by the server: opaque bytes. */
export interface WrappedKey {
  /** Base64. The salt the wrapping key was derived with. */
  salt: string;
  /** Base64. AES-GCM nonce. Never reused with the same wrapping key. */
  nonce: string;
  /** Base64. The family key, encrypted. */
  ciphertext: string;
  /** PBKDF2 iterations, stored so a future increase can still open old wraps. */
  iterations: number;
}

export function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * A fresh family key.
 *
 * `extractable: true` because a key has to be extractable to be wrapped, and
 * wrapping it is the entire point -- one wrap per holder is how a second
 * parent and the wall display ever read anything. See the note on
 * extractability at the top of this file for what that costs and why the
 * alternative does not exist.
 */
export async function createFamilyKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

/** Derive a key from a secret a human typed. */
async function derived(
  secret: string,
  salt: Uint8Array,
  iterations: number,
  usages: KeyUsage[],
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    usages,
  );
}

const wrappingKey = (secret: string, salt: Uint8Array, iterations: number) =>
  derived(secret, salt, iterations, ['wrapKey', 'unwrapKey']);

/**
 * Seal text under a secret somebody typed. Base64 of salt || nonce || ct.
 *
 * Used for one thing: putting the family's name and the sender's email inside
 * an invitation, locked with the invitation code. That is what lets somebody
 * see WHICH family they are about to join before they join it, while the
 * server -- which holds only sha256 of the code -- still cannot read either.
 *
 * Same PBKDF2 and AES-GCM as wrapFamilyKey. The only difference is that the
 * payload is text rather than a key.
 */
export async function sealWithSecret(text: string, secret: string): Promise<string> {
  const salt = randomBytes(SALT_BYTES);
  const nonce = randomBytes(NONCE_BYTES);
  const key = await derived(secret, salt, KDF_ITERATIONS, ['encrypt', 'decrypt']);
  const sealed = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource },
    key,
    new TextEncoder().encode(text),
  );
  const packed = new Uint8Array(salt.length + nonce.length + sealed.byteLength);
  packed.set(salt, 0);
  packed.set(nonce, salt.length);
  packed.set(new Uint8Array(sealed), salt.length + nonce.length);
  return toBase64(packed);
}

export async function openWithSecret(packed: string, secret: string): Promise<string> {
  const bytes = fromBase64(packed);
  const key = await derived(
    secret,
    bytes.slice(0, SALT_BYTES),
    KDF_ITERATIONS,
    ['encrypt', 'decrypt'],
  );
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: bytes.slice(SALT_BYTES, SALT_BYTES + NONCE_BYTES) as BufferSource },
    key,
    bytes.slice(SALT_BYTES + NONCE_BYTES) as BufferSource,
  );
  return new TextDecoder().decode(plain);
}

/** Wrap the family key under a secret: the family code, or a display's link code. */
export async function wrapFamilyKey(key: CryptoKey, secret: string): Promise<WrappedKey> {
  const salt = randomBytes(SALT_BYTES);
  const nonce = randomBytes(NONCE_BYTES);
  const wrapper = await wrappingKey(secret, salt, KDF_ITERATIONS);
  const wrapped = await crypto.subtle.wrapKey('raw', key, wrapper, {
    name: 'AES-GCM',
    iv: nonce as BufferSource,
  });
  return {
    salt: toBase64(salt),
    nonce: toBase64(nonce),
    ciphertext: toBase64(wrapped),
    iterations: KDF_ITERATIONS,
  };
}

/**
 * Open a wrapped family key.
 *
 * Imported extractable, so that a parent who unlocked on a new device can go
 * on to invite somebody else or link a display. A key that arrives unable to
 * be passed on is a dead end one page later.
 *
 * A wrong passphrase fails here as an AES-GCM authentication failure, which is
 * the tag doing its job rather than an error to paper over.
 */
export async function unwrapFamilyKey(wrapped: WrappedKey, secret: string): Promise<CryptoKey> {
  const wrapper = await wrappingKey(
    secret,
    fromBase64(wrapped.salt),
    wrapped.iterations ?? KDF_ITERATIONS,
  );
  try {
    return await crypto.subtle.unwrapKey(
      'raw',
      fromBase64(wrapped.ciphertext) as BufferSource,
      wrapper,
      { name: 'AES-GCM', iv: fromBase64(wrapped.nonce) as BufferSource },
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt'],
    );
  } catch {
    throw new Error('That did not unlock your family. Check what you typed and try again.');
  }
}

/**
 * A wrapped key, as one base64 string.
 *
 * The API validates every key field as base64 and has no opinion about what
 * is inside, which is the property worth keeping: the server stores bytes it
 * cannot interpret. A wrapped key is a small JSON envelope -- salt, nonce,
 * ciphertext, iteration count -- so it gets packed into base64 on the way out
 * and unpacked on the way back.
 *
 * Sending the JSON directly is what the first version did, and the API
 * rejected it with "Recovery key must be base64 text". That error was the
 * validator doing exactly its job.
 */
export function packWrapped(wrapped: WrappedKey): string {
  return toBase64(new TextEncoder().encode(JSON.stringify(wrapped)));
}

export function unpackWrapped(packed: string): WrappedKey {
  return JSON.parse(new TextDecoder().decode(fromBase64(packed))) as WrappedKey;
}

/**
 * Encrypt one field. Returns base64 of nonce || ciphertext.
 *
 * Nonce-prefixed rather than stored separately, because every field in the
 * data model is a single opaque string and keeping the nonce beside the
 * ciphertext means there is no way to store one without the other.
 */
export async function encryptField(key: CryptoKey, plaintext: string): Promise<string> {
  const nonce = randomBytes(NONCE_BYTES);
  const sealed = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource },
    key,
    new TextEncoder().encode(plaintext),
  );
  const packed = new Uint8Array(nonce.length + sealed.byteLength);
  packed.set(nonce, 0);
  packed.set(new Uint8Array(sealed), nonce.length);
  return toBase64(packed);
}

export async function decryptField(key: CryptoKey, packed: string): Promise<string> {
  const bytes = fromBase64(packed);
  const nonce = bytes.slice(0, NONCE_BYTES);
  const sealed = bytes.slice(NONCE_BYTES);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource },
    key,
    sealed as BufferSource,
  );
  return new TextDecoder().decode(plain);
}
